import React from "react";
import {
  Modal,
  ModalBody,
  Offcanvas,
  OffcanvasBody,
} from "../Common/AppOverlay";
import "./WorkflowResponseDrawer.scss";
import "./QueryResponsePage.scss";
import QueryResponseReview from "./QueryResponseReview";
import { parseUtcDateTime } from "../../helpers/utc-date";

const valueOrDash = (value) => value || "—";

const dateTime = (value) =>
  value
    ? parseUtcDateTime(value).toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const initials = (value) =>
  String(value || "User")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

const queryStatus = (value) =>
  /closed/i.test(String(value || ""))
    ? "Closed"
    : /resolved/i.test(String(value || ""))
    ? "Resolved"
    : "Open";

const priorityText = (value) =>
  Number(value) === 3
    ? "High"
    : Number(value) === 1
    ? "Low"
    : String(value || "Medium").replace(/^P\s*/i, "");

const WorkflowResponseDrawer = ({
  inspectionId,
  onReviewPosted,
  response,
  value,
  onChange,
  onClose,
  onSubmit,
  onResolve,
  attachments = [],
  onUpload,
  onDownload,
  uploading = false,
  saving = false,
  progress = null,
  presentation = "drawer",
}) => {
  const isQuery = response?.type === "query";
  const isFindingReview = response?.type === "finding-review";
  const item = response?.item || {};
  const task = response?.task || {};
  const title = isQuery
    ? "Respond to query"
    : isFindingReview
    ? "Review finding response"
    : "Respond to finding";
  const identifier = isQuery
    ? item.queryId || item.id
    : item.findingNumber || item.id;
  const subject = isQuery ? item.subject : item.title;
  const description = isQuery
    ? item.details ||
      item.queryDetails ||
      item.query ||
      item.description ||
      item.Details ||
      item.Query
    : item.description;
  const responseLabel = isQuery ? "Query response" : "Management response";
  const raisedBy =
    item.raisedByName || item.raisedBy || item.createdByName || "Reviewer";
  const assignedTo =
    item.assigneeName ||
    task.assigneeName ||
    task.assignedToName ||
    "Unassigned";

  const displayIdentifier = isQuery
    ? item.taskNumber || identifier
    : identifier;
  const status =
    item.status ||
    item.findingStatus ||
    (isQuery ? "Open" : "Response required");
  const messages = Array.isArray(item.messages) ? item.messages : [];
  const evidenceFiles = item.attachments || item.evidenceFiles || [];

  const fileKey = (file) =>
    file?.id
      ? `id:${file.id}`
      : `local:${file?.name}:${file?.size}:${file?.lastModified || ""}`;

  const uniqueFiles = (files) =>
    files.filter(
      (file, index, items) =>
        file &&
        items.findIndex((candidate) => fileKey(candidate) === fileKey(file)) ===
          index
    );
  const queryFiles = isQuery
    ? uniqueFiles(
        evidenceFiles.filter(
          (file) =>
            String(file.attachmentType || "Query").toLowerCase() !== "response"
        )
      )
    : [];
  const responseMessages = messages.filter(
    (message) => message.type !== "FollowUp"
  );
  const messageFilesByMessage = new Map();
  if (isQuery && responseMessages.length) {
    evidenceFiles
      .filter(
        (file) =>
          file.id &&
          String(file.attachmentType || "").toLowerCase() === "response"
      )
      .forEach((file) => {
        const fileTime = new Date(file.createdOn || 0).getTime();
        const target =
          responseMessages.find(
            (message) => Number(message.id) === Number(file.messageId)
          ) ||
          responseMessages.reduce((best, message) => {
            if (!best) return message;
            const messageTime = new Date(message.createdOn || 0).getTime();
            const bestTime = new Date(best.createdOn || 0).getTime();
            const messageDistance =
              Number.isFinite(fileTime) && Number.isFinite(messageTime)
                ? Math.abs(messageTime - fileTime)
                : 0;
            const bestDistance =
              Number.isFinite(fileTime) && Number.isFinite(bestTime)
                ? Math.abs(bestTime - fileTime)
                : 0;
            return messageDistance < bestDistance ? message : best;
          }, null);
        if (target)
          messageFilesByMessage.set(target.id, [
            ...(messageFilesByMessage.get(target.id) || []),
            file,
          ]);
      });
  }

  const filesForMessage = (message) =>
    uniqueFiles([
      ...(Array.isArray(message.attachments) ? message.attachments : []),
      ...(messageFilesByMessage.get(message.id) || []),
    ]);
  const responseFiles = uniqueFiles([
    ...(isQuery ? [] : evidenceFiles),
    ...attachments,
  ]);
  const isModal = presentation === "modal" || isQuery;
  const useQueryWorkspace = isQuery;
  const Overlay = isModal ? Modal : Offcanvas;
  const OverlayBody = isModal ? ModalBody : OffcanvasBody;
  const overlayProps = isModal
    ? { centered: true, size: isQuery ? "xl" : "lg" }
    : { direction: "end" };
  const progressActive = saving || uploading;
  const progressValue = Number.isFinite(progress?.value)
    ? Math.max(0, Math.min(100, progress.value))
    : null;
  const progressLabel =
    progress?.label ||
    (uploading ? "Uploading attachments…" : "Submitting response…");

  const FileList = ({ files }) =>
    files.length ? (
      <div className="workflow-response-drawer__timeline-files">
        {files.map((file, index) => (
          <article key={file.id || `${file.name}-${file.size}-${index}`}>
            <i className="bx bx-file" />
            <span>
              <b>{file.fileName || file.name}</b>
              <small>
                {Math.max(
                  1,
                  Math.round(Number(file.sizeBytes ?? file.size ?? 0) / 1024)
                )}{" "}
                KB{!file.id ? " · pending" : ""}
              </small>
            </span>
            {file.id && onDownload && (
              <button
                type="button"
                onClick={() => onDownload(file)}
                aria-label={`Download ${file.fileName || file.name}`}
              >
                <i className="bx bx-download" />
              </button>
            )}
          </article>
        ))}
      </div>
    ) : null;

  const QueryWorkspace = () => (
    <div className="workflow-response-drawer__query-workspace">
      <section className="workflow-response-drawer__conversation-column">
        <details className="query-response-page__conversation">
          <summary>Query conversation and previous responses ({messages.length})</summary>
        <div className="workflow-response-drawer__timeline">
          <article className="is-query" data-testid="query-details">
            <span className="workflow-response-drawer__timeline-step">1</span>
            <div className="workflow-response-drawer__message-card">
              <header>
                <span className="workflow-response-drawer__avatar">
                  {initials(raisedBy)}
                </span>
                <span>
                  <b>{raisedBy}</b>
                  <small>{item.raisedByRole || "Reviewer"}</small>
                </span>
                <time>{dateTime(item.createdOn)}</time>
                <em>
                  <i className="bx bx-help-circle" /> Query
                </em>
              </header>
              <p>{valueOrDash(description)}</p>
              {queryFiles.length > 0 && (
                <b className="workflow-response-drawer__timeline-files-label">
                  Query attachments
                </b>
              )}
              <FileList files={queryFiles} />
            </div>
          </article>
          {messages.map((message, index) => {
            const followUp = message.type === "FollowUp";
            const author = message.authorName || "Review participant";
            return (
              <article
                className={followUp ? "is-follow-up" : "is-response"}
                key={message.id || index}
              >
                <span className="workflow-response-drawer__timeline-step">
                  {index + 2}
                </span>
                <div className="workflow-response-drawer__message-card">
                  <header>
                    <span className="workflow-response-drawer__avatar">
                      {initials(author)}
                    </span>
                    <span>
                      <b>{author}</b>
                      <small>
                        {message.authorRole || "Review participant"}
                      </small>
                    </span>
                    <time>{dateTime(message.createdOn)}</time>
                    <em>
                      <i
                        className={`bx ${followUp ? "bx-refresh" : "bx-reply"}`}
                      />{" "}
                      {followUp ? "Re-query" : "Response"}
                    </em>
                  </header>
                  <p>{message.text}</p>
                  <FileList files={filesForMessage(message)} />
                </div>
              </article>
            );
          })}
        </div>
        </details>
        <QueryResponseReview
          key={`${inspectionId || task.inspectionId || response?.reviewId}-${identifier}`}
          inspectionId={inspectionId || task.inspectionId || response?.reviewId}
          queryId={item.queryId || item.id}
          onChanged={onReviewPosted}
        >
        {item.nextMessageType === "FollowUp" && item.canReply && <section className="workflow-response-drawer__composer">
          <header>
            <b>Write a response</b>
          </header>
          <textarea
            id={`query-response-${identifier}`}
            dir="ltr"
            rows="4"
            maxLength="4000"
            value={value}
            onChange={(event) => onChange(event.target.value)}
            placeholder="Type your response here…"
            autoFocus
          />
          <div className="workflow-response-drawer__composer-files">
            <FileList files={responseFiles} />
          </div>
          <footer>
            {onUpload && (
              <label>
                <input
                  type="file"
                  hidden
                  multiple
                  onChange={(event) => {
                    Array.from(event.target.files || []).forEach(onUpload);
                    event.target.value = "";
                  }}
                  disabled={uploading || saving}
                />
                <i
                  className={`bx ${
                    uploading ? "bx-loader-alt bx-spin" : "bx-paperclip"
                  }`}
                />{" "}
                Add attachment
              </label>
            )}
            <small>{value.length}/4000</small>
            <button
              className="is-primary"
              type="button"
              onClick={onSubmit}
              disabled={!value.trim() || saving || uploading}
            >
              <i className="bx bx-send" />{" "}
              {saving ? "Submitting…" : "Send follow-up"}
            </button>
          </footer>
        </section>}
        </QueryResponseReview>
      </section>
      <aside className="workflow-response-drawer__details-rail">
        <section className="workflow-response-drawer__rail-actions">
          <header>
            <i className="bx bx-cog" />
            <b>Actions</b>
          </header>
          <div>
            {onResolve && (
              <button
                className="is-resolve"
                type="button"
                onClick={onResolve}
                disabled={saving || uploading}
              >
                <i className="bx bx-check" /> Mark as Resolved
              </button>
            )}
            {item.canReply && <button
              type="button"
              onClick={() =>
                (document.getElementById(`draft-response-${identifier}`) || document.getElementById(`query-response-${identifier}`))?.focus()
              }
            >
              <i className="bx bx-reply" /> Reply
            </button>}
            <button type="button" onClick={onClose}>
              <i className="bx bx-x" /> Close View
            </button>
          </div>
        </section>
        <section className="query-response-page__context">
          <h3>Description</h3>
          <p>{valueOrDash(description)}</p>
          {queryFiles.length > 0 && <><h3>Query attachments</h3><FileList files={queryFiles} /></>}
        </section>
        <section>
          <header>
            <i className="bx bx-info-circle" />
            <b>Query Details</b>
          </header>
          <dl>
            <div>
              <dt>Query ID</dt>
              <dd>{valueOrDash(displayIdentifier)}</dd>
            </div>
            <div>
              <dt>Inspection</dt>
              <dd>{valueOrDash(task.inspectionNo || task.inspectionName)}</dd>
            </div>
            <div>
              <dt>Checklist Item</dt>
              <dd>
                {valueOrDash(item.questionCode || item.inspectionChecklistId)}
              </dd>
            </div>
            <div>
              <dt>Status</dt>
              <dd>
                <em className="is-status">
                  <i className="bx bx-flag" /> {queryStatus(status)}
                </em>
              </dd>
            </div>
            <div>
              <dt>Priority</dt>
              <dd>
                <em className="is-priority">
                  <i className="bx bx-flag" />{" "}
                  {priorityText(item.priority || task.priority)}
                </em>
              </dd>
            </div>
            <div>
              <dt>Raised by</dt>
              <dd>{raisedBy}</dd>
            </div>
            <div>
              <dt>Assigned to</dt>
              <dd>{assignedTo}</dd>
            </div>
            <div>
              <dt>Created on</dt>
              <dd>{dateTime(item.createdOn)}</dd>
            </div>
            <div>
              <dt>Last updated</dt>
              <dd>{dateTime(item.updatedOn || task.updatedOn)}</dd>
            </div>
          </dl>
        </section>
        <section>
          <header>
            <i className="bx bx-link" />
            <b>Related Information</b>
          </header>
          <dl>
            <div>
              <dt>
                <i className="bx bx-briefcase" /> Engagement
              </dt>
              <dd>
                {valueOrDash(
                  item.engagementName || item.engagement || task.engagementName
                )}
              </dd>
            </div>
            <div>
              <dt>
                <i className="bx bx-check-shield" /> Inspection
              </dt>
              <dd>{valueOrDash(task.inspectionNo || task.inspectionName)}</dd>
            </div>
            <div>
              <dt>
                <i className="bx bx-buildings" /> Client
              </dt>
              <dd>{valueOrDash(item.clientName || task.clientName)}</dd>
            </div>
            {(item.leadReviewerName || task.leadReviewerName) && (
              <div>
                <dt>
                  <i className="bx bx-user" /> Lead Reviewer
                </dt>
                <dd>{item.leadReviewerName || task.leadReviewerName}</dd>
              </div>
            )}
          </dl>
        </section>

      </aside>
    </div>
  );

  return (
    <Overlay
      {...overlayProps}
      isOpen={Boolean(response)}
      toggle={() => !saving && !uploading && onClose()}
      className={`${
        isModal ? "workflow-response-modal" : "workflow-response-drawer"
      } ${
        isQuery
          ? "workflow-response-drawer--query"
          : "workflow-response-drawer--finding"
      }`}
    >
      <header className="workflow-response-drawer__header">
        <div className="workflow-response-drawer__header-title">
          <h2>
            {useQueryWorkspace ? `Query ${displayIdentifier || ""}` : title}
          </h2>
          <span>
            <i
              className={`bx ${
                isQuery ? "bx-message-rounded-dots" : "bx-flag"
              }`}
            />{" "}
            {!useQueryWorkspace &&
              (isQuery
                ? `Query ${displayIdentifier || ""}`
                : `Finding ${displayIdentifier || ""}`)}
            <em>
              {useQueryWorkspace
                ? queryStatus(status)
                : status === "InReview"
                ? "Pending review"
                : status}
            </em>
          </span>
          {useQueryWorkspace && (
            <small>{valueOrDash(item.questionCode || subject)}</small>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          disabled={saving || uploading}
          aria-label={`Close response ${isModal ? "modal" : "drawer"}`}
        >
          <i className="bx bx-x" />
        </button>
      </header>
      {progressActive && (
        <div
          className="workflow-response-drawer__progress"
          role="progressbar"
          aria-label={progressLabel}
          aria-valuemin="0"
          aria-valuemax="100"
          aria-valuenow={progressValue ?? undefined}
        >
          <span>
            <i className="bx bx-loader-alt bx-spin" />
            {progressLabel}
          </span>
          {progressValue !== null && <b>{Math.round(progressValue)}%</b>}
          <div>
            <i
              className={progressValue === null ? "is-indeterminate" : ""}
              style={
                progressValue === null
                  ? undefined
                  : { width: `${progressValue}%` }
              }
            />
          </div>
        </div>
      )}
      <OverlayBody className="workflow-response-drawer__body">
        {useQueryWorkspace ? (
          QueryWorkspace()
        ) : (
          <>
            {isQuery ? (
              <div className="workflow-response-drawer__query-overview">
                <section className="workflow-response-drawer__query-meta">
                  <header>
                    <i className="bx bx-info-circle" />
                    <b>Engagement &amp; inspection</b>
                  </header>
                  <div>
                    <i className="bx bx-briefcase" />
                    <span>
                      <small>Engagement</small>
                      <b>
                        {valueOrDash(item.engagementName || item.engagement)}
                      </b>
                    </span>
                  </div>
                  <div>
                    <i className="bx bx-list-check" />
                    <span>
                      <small>Checklist question</small>
                      <b>
                        {valueOrDash(
                          item.questionCode || item.inspectionChecklistId
                        )}
                      </b>
                    </span>
                  </div>
                  <div>
                    <i className="bx bx-user-pin" />
                    <span>
                      <small>Raised by</small>
                      <b>{valueOrDash(raisedBy)}</b>
                      {item.raisedByRole && <small>{item.raisedByRole}</small>}
                    </span>
                  </div>
                  <div>
                    <i className="bx bx-user" />
                    <span>
                      <small>Assigned to</small>
                      <b>{valueOrDash(item.assigneeName)}</b>
                    </span>
                  </div>
                  <div>
                    <i className="bx bx-flag" />
                    <span>
                      <small>Priority</small>
                      <b>{valueOrDash(item.priority)}</b>
                    </span>
                  </div>
                </section>
                <div className="workflow-response-drawer__query-content">
                  <section className="workflow-response-drawer__query-subject">
                    <small>Query subject</small>
                    <b>{valueOrDash(subject)}</b>
                  </section>
                  <div
                    className="workflow-response-drawer__query-details"
                    data-testid="query-details"
                  >
                    <div className="workflow-response-drawer__query-details-heading">
                      <strong>Details</strong>
                      <span>Raised by {raisedBy}</span>
                    </div>
                    <div className="workflow-response-drawer__query-details-content">
                      {valueOrDash(description)}
                    </div>
                  </div>
                  <section className="workflow-response-drawer__finding-evidence workflow-response-drawer__query-files">
                    <header>
                      <i className="bx bx-paperclip" />
                      <b>Query attachments</b>
                      <small>
                        {queryFiles.length} file
                        {queryFiles.length === 1 ? "" : "s"}
                      </small>
                    </header>
                    {queryFiles.length ? (
                      queryFiles.map((file, index) => (
                        <article
                          key={file.id || `${file.name}-${file.size}-${index}`}
                        >
                          <i className="bx bx-file" />
                          <span>
                            <b>{file.fileName || file.name}</b>
                            <small>
                              {Math.max(
                                1,
                                Math.round(
                                  Number(file.sizeBytes ?? file.size ?? 0) /
                                    1024
                                )
                              )}{" "}
                              KB
                            </small>
                          </span>
                          {file.id && onDownload && (
                            <button
                              type="button"
                              onClick={() => onDownload(file)}
                              aria-label={`Download ${
                                file.fileName || file.name
                              }`}
                            >
                              <i className="bx bx-download" />
                            </button>
                          )}
                        </article>
                      ))
                    ) : (
                      <p className="workflow-response-drawer__empty-files">
                        No files were attached to this query.
                      </p>
                    )}
                  </section>
                </div>
              </div>
            ) : (
              <>
                <section className="workflow-response-drawer__finding-meta">
                  <div>
                    <small>Engagement</small>
                    <b>{valueOrDash(item.engagementName)}</b>
                  </div>
                  <div>
                    <small>Checklist</small>
                    <b>{valueOrDash(item.checklistName)}</b>
                  </div>
                  <div>
                    <small>Question</small>
                    <b>{valueOrDash(item.questionCode)}</b>
                  </div>
                  <div>
                    <small>Assigned to</small>
                    <b>
                      {valueOrDash(
                        item.responseRequestedFromUserName ||
                          item.responseRequestedFromRole ||
                          item.assigneeName
                      )}
                    </b>
                  </div>
                </section>
                <section className="workflow-response-drawer__finding-details">
                  <header>
                    <b>Finding details</b>
                  </header>
                  <dl>
                    <div>
                      <dt>Title</dt>
                      <dd>{valueOrDash(subject)}</dd>
                    </div>
                    <div>
                      <dt>Category</dt>
                      <dd>{valueOrDash(item.category)}</dd>
                    </div>
                    <div>
                      <dt>Severity</dt>
                      <dd>
                        <i
                          className={`severity-dot severity-dot--${String(
                            item.severity || "low"
                          ).toLowerCase()}`}
                        />
                        {valueOrDash(item.severity)}
                      </dd>
                    </div>
                    <div>
                      <dt>Target date</dt>
                      <dd>
                        {item.responseDueDate
                          ? new Date(item.responseDueDate).toLocaleDateString(
                              "en-GB"
                            )
                          : "—"}
                      </dd>
                    </div>
                  </dl>
                  <p>{valueOrDash(description)}</p>
                  {item.rootCause && (
                    <p>
                      <b>Root cause:</b> {item.rootCause}
                    </p>
                  )}
                  {item.recommendation && (
                    <p>
                      <b>Recommendation:</b> {item.recommendation}
                    </p>
                  )}
                </section>
                {isFindingReview && (
                  <section className="workflow-response-drawer__submitted-response">
                    <header>Response from assigned user</header>
                    <p>{valueOrDash(item.managementResponse)}</p>
                    {evidenceFiles.length > 0 && (
                      <div>
                        {evidenceFiles.map((file) => (
                          <span key={file.id}>
                            <i className="bx bx-paperclip" />
                            <b>{file.fileName || file.name}</b>
                          </span>
                        ))}
                      </div>
                    )}
                  </section>
                )}
              </>
            )}
            {isQuery && messages.length > 0 && (
              <section className="workflow-response-drawer__query-thread">
                <header>
                  <i className="bx bx-conversation" />
                  <b>Conversation</b>
                </header>
                {messages.map((message) => (
                  <article
                    className={
                      message.type === "FollowUp"
                        ? "is-follow-up"
                        : "is-response"
                    }
                    key={message.id}
                  >
                    <i
                      className={`bx ${
                        message.type === "FollowUp"
                          ? "bx-message-add"
                          : "bx-reply"
                      }`}
                    />
                    <div>
                      <header>
                        <b>
                          {message.type === "FollowUp"
                            ? "Follow-up query"
                            : "Response"}
                        </b>
                        <span>
                          {message.authorName || "Review participant"}
                          {message.createdOn
                            ? ` · ${new Date(message.createdOn).toLocaleString(
                                "en-GB",
                                {
                                  day: "2-digit",
                                  month: "short",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                }
                              )}`
                            : ""}
                        </span>
                      </header>
                      <p>{message.text}</p>
                      <FileList files={filesForMessage(message)} />
                    </div>
                  </article>
                ))}
              </section>
            )}
            <label
              className={`workflow-response-drawer__response ${
                isFindingReview ? "is-review" : ""
              }`}
            >
              <b>
                {isQuery
                  ? "Your Reply"
                  : isFindingReview
                  ? "Reviewer comments"
                  : responseLabel}{" "}
                <em>*</em>
              </b>
              <textarea
                rows="9"
                maxLength="4000"
                value={value}
                onChange={(event) => onChange(event.target.value)}
                placeholder={
                  isQuery
                    ? "Enter your reply to this query..."
                    : isFindingReview
                    ? "Record why this response resolves the finding..."
                    : "Describe the action taken and supporting information..."
                }
                autoFocus
              />
              <small className="workflow-response-drawer__response-count">
                {value.length}/4000
              </small>
            </label>
            {!isFindingReview && (
              <section className="workflow-response-drawer__finding-evidence">
                <header>
                  <i className="bx bx-paperclip" />
                  <b>{isQuery ? "Reply attachments" : "Attachments"}</b>
                  <small>
                    {responseFiles.length} file
                    {responseFiles.length === 1 ? "" : "s"}
                  </small>
                </header>
                {responseFiles.map((file, index) => (
                  <article
                    key={file.id || `${file.name}-${file.size}-${index}`}
                  >
                    <i className="bx bx-file" />
                    <span>
                      <b>{file.fileName || file.name}</b>
                      <small>
                        {Math.max(
                          1,
                          Math.round(
                            Number(file.sizeBytes ?? file.size ?? 0) / 1024
                          )
                        )}{" "}
                        KB{!file.id ? " · pending" : ""}
                      </small>
                    </span>
                    {file.id && onDownload && (
                      <button
                        type="button"
                        onClick={() => onDownload(file)}
                        aria-label={`Download ${file.fileName || file.name}`}
                      >
                        <i className="bx bx-download" />
                      </button>
                    )}
                  </article>
                ))}
                {onUpload && (
                  <label className="workflow-response-drawer__upload">
                    <input
                      type="file"
                      hidden
                      multiple
                      onChange={(event) => {
                        Array.from(event.target.files || []).forEach(onUpload);
                        event.target.value = "";
                      }}
                      disabled={uploading || saving}
                    />
                    <i
                      className={`bx ${
                        uploading ? "bx-loader-alt bx-spin" : "bx-cloud-upload"
                      }`}
                    />
                    <span>
                      <b>{uploading ? "Uploading…" : "Browse Files"}</b>
                      <small>
                        {isQuery
                          ? "Attach supporting files to your reply"
                          : "Add evidence to this response"}
                      </small>
                    </span>
                  </label>
                )}
              </section>
            )}
            {isQuery && (
              <p className="workflow-response-drawer__resolve-note">
                <i className="bx bx-conversation" />
                <span>
                  <b>This reply continues the query conversation.</b>
                  <small>
                    The query remains open until the reviewer or query raiser
                    explicitly closes it.
                  </small>
                </span>
              </p>
            )}
            {!isQuery && !isFindingReview && (
              <p className="workflow-response-drawer__resolve-note">
                <i className="bx bx-check-square" />
                <span>
                  <b>Submit the response for independent review.</b>
                  <small>
                    Your response completes the assigned task and moves the
                    finding to In Review. It does not resolve the finding.
                  </small>
                </span>
              </p>
            )}
            {isFindingReview && (
              <p className="workflow-response-drawer__review-note">
                <i className="bx bx-check-circle" />
                <span>
                  <b>Accept response and close finding</b>
                  <small>
                    The response and reviewer comments remain in the finding
                    audit history.
                  </small>
                </span>
              </p>
            )}
          </>
        )}
      </OverlayBody>
      {!useQueryWorkspace && (
        <footer className="workflow-response-drawer__footer">
          <button
            type="button"
            onClick={onClose}
            disabled={saving || uploading}
          >
            Cancel
          </button>
          <button
            className="is-primary"
            type="button"
            onClick={onSubmit}
            disabled={!value.trim() || saving || uploading}
          >
            <i className={`bx ${isFindingReview ? "bx-check" : "bx-send"}`} />{" "}
            {saving
              ? "Submitting…"
              : isQuery
              ? "Post Response"
              : isFindingReview
              ? "Resolve & Close"
              : "Post Response"}
          </button>
        </footer>
      )}
    </Overlay>
  );
};

export default WorkflowResponseDrawer;
